import { useMemo, useState } from "react";
import styled from "styled-components";
import { Unfold } from "@xfrontend/xicon";
import { isEntra } from "#/modules/auth/idp";
import { useOrg } from "#/modules/auth/OrgContext";
import { useRbac } from "#/modules/auth/useRbac";
import OrgSwitchModal from "#/modules/auth/OrgSwitchModal";

// Borderless/transparent header button with hover background and the Unfold (⇅) icon.
// Shows the Organization name (bold) over its Territory (muted) — same hierarchy as the cards.
const OrgNameWrap = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 320px;
  padding: 4px 8px;
  margin-left: 12px;
  border: none;
  background: none;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  &:hover { background-color: var(--x-purple-500, #494358); }
  .stack { display: flex; flex-direction: column; min-width: 0; line-height: 1.25; }
  .name {
    color: var(--x-gray-200, #f6f6f7); font-size: 14px; font-weight: 600;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .territory {
    color: var(--x-gray-400, #cfd0d3); font-size: 11.5px;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
`;

// Top-left Organization entry point. Always shows the active org (name + Territory) as a
// clickable switcher that opens the org panel — even with a single org.
const OrgSwitcher = (): JSX.Element | null => {
  const { isSystemAdmin, canEditOrg } = useRbac();
  const {
    orgs, activeOrgId, defaultOrgId, switchOrg, setDefaultOrg,
  } = useOrg();
  const [open, setOpen] = useState(false);

  // Jump to the management page pre-filtered to the chosen org (SYSTEM_ADMIN only — the license/
  // management mutating APIs are SA-only, so non-admins would only hit 403). A full reload
  // (not SPA navigate) is required: when already on /management, React won't remount and the
  // ?license deep-link — read once on mount — would never re-apply the Scope filter.
  const manageOrg = isSystemAdmin
    ? (licenseId: string): void => { window.location.assign(`/management?license=${licenseId}`); }
    : undefined;

  // Open the org editor for the chosen org (system admins only). Same full-reload reasoning.
  const editOrg = canEditOrg
    ? (licenseId: string): void => { window.location.assign(`/management?license=${licenseId}&edit=1`); }
    : undefined;

  const current = useMemo(
    () => orgs.find((o) => o.license_id === activeOrgId),
    [orgs, activeOrgId],
  );
  const orgName = current?.license_name ?? activeOrgId ?? "";
  const territory = current?.customer_name ?? "";

  // Render only once the org list has loaded — showing it earlier flashes the "Organization"
  // placeholder before the real name resolves. Entra + an active org required.
  // Always clickable, even with a single org — the panel still shows license details (status,
  // expiry, role) and "Set as default"; it just won't switch to anything else.
  if (!isEntra || !activeOrgId || orgs.length === 0) return null;

  return (
    <>
      <OrgNameWrap
        type="button"
        onClick={() => setOpen(true)}
        title={territory ? `${orgName} · ${territory}` : orgName}
      >
        <span className="stack">
          <span className="name">{orgName}</span>
          {territory && <span className="territory">{territory}</span>}
        </span>
        <Unfold size={14} color="var(--x-gray-200, #f6f6f7)" />
      </OrgNameWrap>
      <OrgSwitchModal
        show={open}
        forced={false}
        orgs={orgs}
        activeOrgId={activeOrgId}
        defaultOrgId={defaultOrgId}
        onSwitch={switchOrg}
        onSetDefault={setDefaultOrg}
        onManageOrg={manageOrg}
        onEditOrg={editOrg}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

export default OrgSwitcher;
